import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Rating,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  VerifiedUser as VerifiedIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const DriverProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [driver, setDriver] = useState<any>(null);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadDriverProfile();
    }
  }, [id]);

  const loadDriverProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.get(`/users/${id}`);
      if (response.success) {
        setDriver(response.data.user);
        // Load vehicles if driver has vehicles
        if (response.data.user.vehicles) {
          setVehicles(response.data.user.vehicles);
        } else {
          // Try to load vehicles separately
          try {
            const vehiclesResponse = await apiService.get(`/vehicles/driver/${id}`);
            if (vehiclesResponse.success) {
              setVehicles(vehiclesResponse.data.vehicles || []);
            }
          } catch (vehiclesError) {
            console.error('Error loading vehicles:', vehiclesError);
          }
        }
      } else {
        setError(response.message || 'Failed to load driver profile');
      }
    } catch (err: any) {
      console.error('Load driver profile error:', err);
      setError(err.response?.data?.message || 'Failed to load driver profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !driver) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error || 'Driver not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, fontSize: '1.5em' }}
      >
        Back
      </Button>

      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
          <Avatar
            src={driver.profilePicture}
            sx={{ width: 100, height: 100 }}
          >
            {driver.firstName[0]}{driver.lastName[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4" sx={{ fontSize: '1.5em' }}>
                {driver.firstName} {driver.lastName}
              </Typography>
              {driver.isLicenseVerified && (
                <VerifiedIcon sx={{ color: '#4caf50' }} />
              )}
            </Box>
            {driver.driverRating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rating value={driver.driverRating} readOnly precision={0.1} />
                <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                  {driver.driverRating.toFixed(1)} ({driver.totalRides || 0} rides)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.5em' }}>
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
              {driver.email}
            </Typography>
          </Grid>
          {driver.phoneNumber && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.5em' }}>
                Phone
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                {driver.phoneNumber}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Vehicles Section */}
      {vehicles.length > 0 && (
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5em', mb: 3 }}>
            <CarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Vehicles
          </Typography>
          <Grid container spacing={2}>
            {vehicles.map((vehicle) => (
              <Grid size={{ xs: 12 }} key={vehicle.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '1.5em', mb: 1 }}>
                          {vehicle.color} {vehicle.make?.name} {vehicle.model?.name} ({vehicle.year})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                          License Plate: {vehicle.licensePlate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                          Seats: {vehicle.seats}
                        </Typography>
                      </Box>
                      {vehicle.status === 'APPROVED' && (
                        <Chip label="Verified" color="success" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export default DriverProfilePage;

